import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { filterMap } from '@interacta-shared/util';
import { LoadingLayerService } from '@interacta-shared/util-common';
import { PostType } from '@interacta-shared/util-post-lite';
import {
    createPostFromLabContents,
    creditLimitExceededError,
    isCreditLimitExceededError,
    openILabPostDocument,
    openILabPostLightbox,
    openILabPostPreview,
} from '@interacta/data-access-i-lab';
import { PostPreviewOverlaySource } from '@modules/post-preview/models/post-preview.model';
import { PostPreviewService } from '@modules/post-preview/services/post-preview.service';
import { PostFormActions } from '@modules/post/store/form';
import { selectPostFormFeature } from '@modules/post/store/form/post-form.selectors';
import { SimpleLightBoxService } from '@modules/simple-light-box/services/simple-light-box.service';
import { GalleryStateService } from '@modules/state/services/gallery-state.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concatMap, filter, from, map, switchMap, tap } from 'rxjs';
import { ILabInteropCommunitySelectionDialogStateService } from '../services/i-lab-interop-community-selection-dialog-state.service';
import * as ILabInteropActions from './i-lab-interop.actions';
import { selectILabId } from './i-lab-interop.selectors';

@Injectable()
export class ILabInteropEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly iLabInteropCommunitySelectionDialogStateService = inject(
        ILabInteropCommunitySelectionDialogStateService,
    );
    private readonly loadingLayerService = inject(LoadingLayerService);
    private readonly simpleLightBoxService = inject(SimpleLightBoxService);
    private readonly postPreviewService = inject(PostPreviewService);
    private readonly galleryStateService = inject(GalleryStateService);

    createPostFromLabContents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(createPostFromLabContents),
            switchMap((action) =>
                this.iLabInteropCommunitySelectionDialogStateService
                    .open()
                    .pipe(map((community) => ({ community, action }))),
            ),
            filterMap(({ community, action }) =>
                community ? { community, action } : undefined,
            ),
            concatMap(({ community, action }) =>
                from([
                    ILabInteropActions.setLabId({ labId: action.labId }),
                    ILabInteropActions.setContentIds({
                        contentIds: action.contentIds,
                    }),
                    ILabInteropActions.prepareCreatePost({
                        communityId: community.id,
                    }),
                ]),
            ),
        ),
    );

    prepareCreatePost$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ILabInteropActions.prepareCreatePost),
            concatLatestFrom(() => this.store.select(selectILabId)),
            tap(() =>
                this.loadingLayerService.open({
                    text: ['TEXT_1', 'TEXT_2', 'TEXT_3'].map(
                        (text) => `I_LAB.POST_GENERATION_PENDING.${text}`,
                    ),
                    imageSrc:
                        'assets/shared/images/ai/ai-interacta-loader-animation_D.gif',
                    secondaryText: '',
                }),
            ),
            map(([{ communityId }, labId]) =>
                PostFormActions.initCreateForm({
                    communityId,
                    q: null,
                    from: null,
                    postType: PostType.CUSTOM,
                    originalPostId: null,
                    labId,
                }),
            ),
        ),
    );

    openPostILabSummaryPage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(PostFormActions.setPost),
                concatLatestFrom(() =>
                    this.store.select(selectPostFormFeature),
                ),
                filterMap(([, state]) =>
                    !!state.labId && state.community?.id != null
                        ? state.community.id
                        : undefined,
                ),
                tap((communityId) => {
                    this.loadingLayerService.close();
                    this.router.navigate(['post', 'i-lab', communityId]);
                }),
            ),
        { dispatch: false },
    );

    // Viewing seam for the source post (the post used as an iLab source)
    // mounted in the iLab detail: the lib PostCoreDetail
    // emits, the feature lib dispatches, these effects reach the
    // app-only services.
    openILabPostLightbox$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(openILabPostLightbox),
                tap((event) => this.simpleLightBoxService.openLightbox(event)),
            ),
        { dispatch: false },
    );

    openILabPostPreview$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(openILabPostPreview),
                tap(({ postId }) =>
                    this.postPreviewService.openPostPreview({
                        source: PostPreviewOverlaySource.Static,
                        idPosts: [postId],
                    }),
                ),
            ),
        { dispatch: false },
    );

    openILabPostDocument$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(openILabPostDocument),
                tap((event) => {
                    this.galleryStateService.openGallery(
                        {
                            feature: 'embedded',
                            attachmentList: event.attachments,
                        },
                        event.index,
                    );
                    this.galleryStateService.canEdit(false);
                }),
            ),
        { dispatch: false },
    );

    // HANDLE ERRORS
    closeLoadingLayer$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(PostFormActions.getPostForCreateError),
                tap(() => this.loadingLayerService.close()),
            ),
        { dispatch: false },
    );

    // AI post generation blocked by the installation credit limit: raise the
    // i-lab credit flag so the dedicated message and banner are shown.
    creditLimitExceededOnCreatePost$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PostFormActions.getPostForCreateError),
            filter(({ error }) => isCreditLimitExceededError(error)),
            map(() => creditLimitExceededError()),
        ),
    );
}
